import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API } from "@/App";
import { 
  Search, User, Activity, TrendingUp, TrendingDown,
  Clock, AlertTriangle, Flag, ChevronLeft, ChevronRight
} from "lucide-react";
import { toast } from "sonner";

const PartnerPatients = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [complianceFilter, setComplianceFilter] = useState(searchParams.get("filter") || "all");

  const limit = 20;

  useEffect(() => {
    fetchPatients();
  }, [token, page]);

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API}/partner-admin/patients?skip=${page * limit}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setPatients(data.patients || []);
        setTotal(data.total || 0);
      } else {
        toast.error("Failed to load patients");
      }
    } catch (error) {
      console.error("Fetch patients error:", error);
      toast.error("Failed to load patients");
    } finally {
      setLoading(false);
    }
  };

  const getComplianceBadge = (score) => {
    if (score >= 71) return { label: "Good", color: "bg-success/10 text-success" };
    if (score >= 41) return { label: "Moderate", color: "bg-warning/10 text-warning" };
    return { label: "Poor", color: "bg-destructive/10 text-destructive" };
  };

  const getHba1cBadge = (hba1c) => {
    if (!hba1c) return { color: "text-muted-foreground" };
    if (hba1c < 7) return { color: "text-success" };
    if (hba1c < 8) return { color: "text-warning" };
    return { color: "text-destructive" };
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return "Never";
    const now = new Date();
    const then = new Date(timestamp);
    const diffHours = Math.floor((now - then) / (1000 * 60 * 60));
    
    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "Yesterday";
    return `${diffDays} days ago`;
  };

  const isInactive = (timestamp) => {
    if (!timestamp) return true;
    const now = new Date();
    const then = new Date(timestamp);
    const diffDays = Math.floor((now - then) / (1000 * 60 * 60 * 24));
    return diffDays >= 3;
  };

  // Filter patients
  let filteredPatients = patients.filter(
    (p) =>
      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (complianceFilter !== "all") {
    filteredPatients = filteredPatients.filter((p) => {
      const score = p.compliance_score || 0;
      if (complianceFilter === "good") return score >= 71;
      if (complianceFilter === "moderate") return score >= 41 && score < 71;
      if (complianceFilter === "poor") return score < 41;
      if (complianceFilter === "flagged") return score < 41 || isInactive(p.last_activity);
      if (complianceFilter === "active") return !isInactive(p.last_activity);
      if (complianceFilter === "hba1c") return !!p.estimated_hba1c;
      return true;
    });
  }

  const totalPages = Math.ceil(total / limit);

  // Count flagged patients
  const flaggedCount = patients.filter(
    (p) => (p.compliance_score || 0) < 41 || isInactive(p.last_activity)
  ).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Active Filter Indicator */}
      {complianceFilter !== "all" && (
        <div className="flex items-center gap-2">
          <Badge className="bg-primary/10 text-primary">
            Filter: {complianceFilter === "good" ? "Good Compliance" : complianceFilter === "moderate" ? "Moderate" : complianceFilter === "poor" ? "Poor/Needs Attention" : complianceFilter === "flagged" ? "Flagged" : complianceFilter === "active" ? "Active (7 days)" : complianceFilter === "hba1c" ? "With HbA1c Data" : complianceFilter} ({filteredPatients.length})
          </Badge>
          <Button variant="ghost" size="sm" onClick={() => setComplianceFilter("all")} className="text-xs h-6">Clear</Button>
        </div>
      )}
      
      {/* Flagged Alert */}
      {flaggedCount > 0 && (
        <Card className="p-4 rounded-2xl border-destructive/20 bg-destructive/5">
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-3">
              <Flag className="w-5 h-5 text-destructive" />
              <div>
                <p className="font-medium text-destructive">
                  {flaggedCount} Flagged Patient(s)
                </p>
                <p className="text-sm text-muted-foreground">
                  Patients with poor compliance or no activity in 3+ days
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-destructive/30 text-destructive hover:bg-destructive/10"
              onClick={() => setComplianceFilter("flagged")}
            >
              View Flagged
            </Button>
          </div>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search patients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl border-orange-100"
          />
        </div>
        <Select value={complianceFilter} onValueChange={setComplianceFilter}>
          <SelectTrigger className="w-[180px] rounded-xl border-orange-100">
            <SelectValue placeholder="Filter by compliance" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Patients</SelectItem>
            <SelectItem value="good">Good Compliance</SelectItem>
            <SelectItem value="moderate">Moderate Compliance</SelectItem>
            <SelectItem value="poor">Poor Compliance</SelectItem>
            <SelectItem value="flagged">Flagged</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Patients Table */}
      <Card className="rounded-2xl border-orange-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-green-50/50">
              <tr>
                <th className="text-left p-3 sm:p-4 font-medium text-secondary text-xs sm:text-sm">Patient</th>
                <th className="text-left p-3 sm:p-4 font-medium text-secondary text-xs sm:text-sm">Compliance</th>
                <th className="text-left p-3 sm:p-4 font-medium text-secondary text-xs sm:text-sm hidden md:table-cell">Last Activity</th>
                <th className="text-left p-3 sm:p-4 font-medium text-secondary text-xs sm:text-sm hidden md:table-cell">Risk Level</th>
                <th className="text-left p-3 sm:p-4 font-medium text-secondary text-xs sm:text-sm hidden lg:table-cell">Est. HbA1c</th>
                <th className="text-left p-3 sm:p-4 font-medium text-secondary text-xs sm:text-sm hidden sm:table-cell">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredPatients.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-muted-foreground">
                    No patients found.
                  </td>
                </tr>
              ) : (
                filteredPatients.map((patient) => {
                  const compliance = getComplianceBadge(patient.compliance_score || 0);
                  const hba1c = getHba1cBadge(patient.estimated_hba1c);
                  const inactive = isInactive(patient.last_activity);
                  const flagged = (patient.compliance_score || 0) < 41 || inactive;
                  
                  return (
                    <tr 
                      key={patient.user_id} 
                      className={`border-t border-orange-100 hover:bg-green-50/30 cursor-pointer ${
                        flagged ? "bg-destructive/5" : ""
                      }`}
                      onClick={() => navigate(`/partner/patient/${patient.user_id}`)}
                      data-testid={`patient-row-${patient.user_id}`}
                    >
                      <td className="p-3 sm:p-4">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="relative shrink-0">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center">
                              <User className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                            </div>
                            {flagged && (
                              <div className="absolute -top-1 -right-1 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-destructive rounded-full flex items-center justify-center">
                                <Flag className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-secondary text-sm truncate">{patient.name || "Unknown"}</p>
                            <p className="text-xs text-muted-foreground truncate">{patient.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 sm:p-4">
                        <span className={`px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium ${compliance.color}`}>
                          {patient.compliance_score || 0}%
                        </span>
                      </td>
                      <td className="p-3 sm:p-4 hidden md:table-cell">
                        <div className={`flex items-center gap-2 text-sm ${
                          inactive ? "text-destructive" : "text-muted-foreground"
                        }`}>
                          <Clock className="w-4 h-4" />
                          {getTimeAgo(patient.last_activity)}
                        </div>
                      </td>
                      <td className="p-3 sm:p-4 hidden md:table-cell">
                        {(patient.compliance_score || 0) < 41 ? (
                          <span className="flex items-center gap-1 text-destructive font-medium">
                            <AlertTriangle className="w-4 h-4" />
                            High Risk
                          </span>
                        ) : (patient.compliance_score || 0) < 71 ? (
                          <span className="flex items-center gap-1 text-warning font-medium">
                            <Activity className="w-4 h-4" />
                            Moderate
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-success font-medium">
                            <TrendingUp className="w-4 h-4" />
                            Low Risk
                          </span>
                        )}
                      </td>
                      <td className="p-3 sm:p-4 hidden lg:table-cell">
                        <span className={`font-medium ${hba1c.color}`}>
                          {patient.estimated_hba1c ? `${patient.estimated_hba1c}%` : "N/A"}
                        </span>
                      </td>
                      <td className="p-3 sm:p-4 hidden sm:table-cell">
                        {inactive ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            Inactive
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                            Active
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-full"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm">
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="rounded-full"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerPatients;
